import type { FC } from 'react';
import React, { useState, useCallback } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import EmptyState from '@atlaskit/empty-state/empty-state';
import Button from '@atlaskit/button/loading-button';

import GenericErrorImg from './GenericErrorImg.svg';

export const i18n = defineMessages({
	errorHeader: {
		id: 'feed.error.pagination.header',
		defaultMessage: 'We couldn’t load more content',
		description:
			'error message header that is presented when a data error stops us from rendering the next page of data (infinite scroll)',
	},
	buttonText: {
		id: 'feed.error.pagination.button.text',
		defaultMessage: 'Try again',
		description: 'action on error message that allows the user to retry the data request',
	},
});

type Props = {
	refetch: () => Promise<void>;
} & WrappedComponentProps;

const PaginationFeedErrorComponent: FC<Props> = ({ refetch, intl }) => {
	const [loading, setLoading] = useState(false);

	const handleRefresh = useCallback(async () => {
		setLoading(true);
		await refetch();
		setLoading(false);
	}, [refetch, setLoading]);

	const refreshButton = (
		<Button onClick={handleRefresh} isLoading={loading}>
			<FormattedMessage {...i18n.buttonText} />
		</Button>
	);

	return (
		<EmptyState
			header={intl.formatMessage(i18n.errorHeader)}
			imageUrl={GenericErrorImg}
			imageHeight={95}
			primaryAction={refreshButton}
		/>
	);
};

export const PaginationFeedError = injectIntl(PaginationFeedErrorComponent);
